import {
  LOCALE_ID,
  Inject,
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Input,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import { formatDate } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { FormGroup, FormBuilder, FormArray } from '@angular/forms';
import { merge, Subscription } from 'rxjs';
import { Product, ProductElement, ProductQuery } from '@models/product';
import { Vendor } from '@models/vendor';
import { OrderForm } from '@classes/order-form';
import { Order } from '@models/order';
import { Patient } from '@models/patient';
import { AuthService } from '@services/auth.service';
import { OrderService } from '@services/order.service';
import { ProductService } from '@services/product.service';
import { StoreService } from '@services/store.service';
import { SettingsService } from '@services/settings.service';
import { MaterialOption } from '@models/settings';
import { MatDialog } from '@angular/material/dialog';
import { RepairOrderDialogComponent, RepairPopupResult } from '@components/admin/patient/repair-order-dialog/repair-order-dialog.component';
import { HoldFollowUpDialogComponent, HoldRxPopupResult } from '@components/admin/patient/hold-follow-up-dialog/hold-follow-up-dialog.component';
import { GenericNotesDialogComponent } from '@components/admin/order/task-popups/generic-notes-dialog.component';
import { CallRxDialogComponent } from '@components/admin/order/task-popups/call-rx-dialog.component';
import { TaskActivityComponent } from '@components/admin/order/task-activity/task-activity.component';
import { NOSEPAD_INSTALL_ITEM, OTHER_REPAIR_ITEM, REPAIR_ITEM_OPTIONS, normalizeRepairItems } from '../../../../constants/repair-items';
import { IN_HOUSE_REPAIR_LOCATION, isOutsideRepairLocation } from '../../../../constants/task-lists';
import {
  FIRST_INSPECTION_NEEDED,
  ORDER_LENSES_ACTION,
  TASK_STATUS_BY_ACTION,
  combinedAwaitingStatus,
  exclusiveJobAction,
  hasJobAction,
  isOpenTaskStatus,
  jobActionList,
  taskDisplayName,
  toggleJobAction,
} from '@classes/job-actions';
import { stackStatusForType } from '@classes/order-save';

export interface FrameNoteEntry {
  timestamp: string;
  initials: string;
  text: string;
  legacy?: boolean;
}

@Component({
  selector: 'app-frame-details',
  templateUrl: './frame-details.component.html',
  styleUrls: ['./frame-details.component.css', '../order.component.css'],
})
export class FrameDetailsComponent implements OnInit, OnDestroy {
  @Input() order: Order = new Order();
  @Input() orderForm: FormGroup;
  @Input() patient: Patient;
  @Input() authorized = false;
  @Output() saveOrder = new EventEmitter<{ destination: string }>();
  @ViewChild(TaskActivityComponent) taskActivity?: TaskActivityComponent;

  storeOptions: any[];
  typeOptions: Object[] = [
    { viewValue: 'RX', value: 'RX' },
    { viewValue: 'SUN', value: 'SUN' },
    { viewValue: 'OTHER', value: 'OTHER' },
  ];
  preOrderStatusOptions = ['TO ORDER', 'ENCLOSED', 'CUT & EDGE'];
  orderFrameStatusOptions = [
    'ORDER FRAME',
    'ORDER PART',
    'ORDER ACCESSORY',
    'RECEIVED',
    'DISPENSED',
    'CANCELED',
    'INQUIRY',
    'RETURNED',
    'RETURN',
    'RETURNING',
    'RE-STYLED',
  ];

  get frameStatusSelectOptions(): string[] {
    const current = String(this.orderForm?.get('status')?.value || '').trim();
    if (current && !this.orderFrameStatusOptions.includes(current)) {
      return [current, ...this.orderFrameStatusOptions];
    }
    return this.orderFrameStatusOptions;
  }
  followUpStatusOptions = ['RETURNED', 'WARRANTY REPLACEMENT', 'REPAIR'];
  physicalStatusOptions = ['ENCLOSED', 'ORDERED', 'ON-APPROVAL', 'TO ORDER'];
  private readonly baseJobActionOptions = [
    ORDER_LENSES_ACTION,
    'HOLD / RX',
    'HOLD',
    'CALL FOR RX',
    'INQUIRY',
    'REPAIR',
  ];
  private static readonly ON_APPROVAL_SHIPPING = 20;
  private static readonly DEFAULT_SHIP_CHARGE = 20;
  private static readonly DEFAULT_ETA_DAYS = 14;
  private lastEtaMs: number | null = null;
  shipCarrierOptions = ['UPS', 'FedEx', 'USPS', 'Other'];
  materialOptions: MaterialOption[] = [];
  selectedProduct: Product = null;
  frameNoteDraft = '';
  private pricingSubscription: Subscription;
  private savedFrameRetail: number | null = null;
  private static readonly FRAME_NOTE_ENTRY_START =
    /\[(\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}\s*[AaPp][Mm])\s*-\s*([^\]]+)\]/g;

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    public auth: AuthService,
    public orderService: OrderService,
    public productService: ProductService,
    public storeService: StoreService,
    public settingsService: SettingsService,
    public cdRef: ChangeDetectorRef,
    public fb: FormBuilder,
    public route: ActivatedRoute,
    private dialog: MatDialog
  ) {
    this.storeOptions = this.storeService.stores;
  }

  ngOnInit(): void {
    if (this.orderForm) {
      this.ensureFrameStatusControls();
      this.initSelectedProductFromForm();
      this.setupCustomPricingWatchers();
      this.setupSizeToMeasurementSync();
      this.formatPricingFields();
      this.settingsService.loadSettings().subscribe({
        next: (settings) => {
          this.materialOptions = settings.materials || [];
          this.ensureSelectedMaterialOption();
          const material = this.orderForm?.get('products.0.frame.material')?.value;
          if (material) {
            this.syncMaterialPrice(material);
          }
          this.updateCustomAmount();
        },
        error: () => this.updateCustomAmount(),
      });
      return;
    }

    if (!this.order.products || !this.order.products[0]) {
      this.order.products[0] = new ProductElement();
    }
    this.orderForm = OrderForm.initOrderFormGroup(
      this.order,
      this.auth,
      this.storeService.stores
    );
    this.ensureFrameStatusControls();
    this.setupCustomPricingWatchers();
    this.setupSizeToMeasurementSync();
    this.formatPricingFields();
    this.settingsService.loadSettings().subscribe({
      next: (settings) => {
        this.materialOptions = settings.materials || [];
        this.ensureSelectedMaterialOption();
        const material = this.orderForm?.get('products.0.frame.material')?.value;
        if (material) {
          this.syncMaterialPrice(material);
        }
        this.updateCustomAmount();
      },
      error: () => this.updateCustomAmount(),
    });
    this.cdRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.pricingSubscription?.unsubscribe();
  }

  setupCustomPricingWatchers(): void {
    if (!this.orderForm) {
      return;
    }

    this.pricingSubscription?.unsubscribe();
    this.pricingSubscription = new Subscription();

    const product = this.orderForm.get('products.0');
    const frame = product?.get('frame');
    if (!product || !frame) {
      return;
    }

    const partOtherDescription = frame.get('partOtherDescription');
    const partOtherPrice = frame.get('partOtherPrice');
    const syncPartOtherFields = (enabled: boolean) => {
      if (enabled) {
        partOtherDescription.enable({ emitEvent: false });
        partOtherPrice.enable({ emitEvent: false });
      } else {
        partOtherDescription.disable({ emitEvent: false });
        partOtherPrice.disable({ emitEvent: false });
        partOtherDescription.setValue('', { emitEvent: false });
        partOtherPrice.setValue(null, { emitEvent: false });
      }
    };

    syncPartOtherFields(!!frame.get('partOther').value);

    this.pricingSubscription.add(
      frame.get('partOther').valueChanges.subscribe((checked) => {
        if (checked) {
          frame.get('partOther').setValue(false, { emitEvent: false });
          syncPartOtherFields(false);
          this.updateCustomAmount();
          this.promptRepairForSelection([OTHER_REPAIR_ITEM]);
          return;
        }
        syncPartOtherFields(false);
        this.updateCustomAmount();
        this.removeRepairItems([OTHER_REPAIR_ITEM]);
        this.refreshTaskActivity();
      })
    );

    this.pricingSubscription.add(
      frame.get('pof').valueChanges.subscribe((pof) => {
        this.syncPofFrameCost(pof);
        this.syncPofPhysicalStatus(pof);
      })
    );

    if (frame.get('pof').value) {
      this.syncPofFrameCost(true);
    }

    this.pricingSubscription.add(
      frame.get('material').valueChanges.subscribe((label) => {
        this.syncMaterialPrice(label);
        this.updateCustomAmount();
      })
    );

    this.pricingSubscription.add(
      merge(
        ...[
          frame.get('duty').valueChanges,
          frame.get('nosepadInstall').valueChanges,
          frame.get('partOtherPrice').valueChanges,
          frame.get('physicalStatus')?.valueChanges,
        ].filter(Boolean)
      ).subscribe(() => {
        this.syncProductDutyFlag();
        this.updateCustomAmount();
      })
    );

    this.pricingSubscription.add(
      frame.get('nosepadInstall').valueChanges.subscribe((checked) => {
        if (checked) {
          frame.get('nosepadInstall').setValue(false, { emitEvent: false });
          this.updateCustomAmount();
          this.promptRepairForSelection([NOSEPAD_INSTALL_ITEM]);
          return;
        }
        this.removeRepairItems([NOSEPAD_INSTALL_ITEM]);
        this.refreshTaskActivity();
      })
    );

    // Tax / Frame / Custom edits only recalculate Subtotal (Custom stays user-editable).
    this.pricingSubscription.add(
      merge(
        ...[
          product.get('tax').valueChanges,
          product.get('retail').valueChanges,
          product.get('custom').valueChanges,
          product.get('shipping')?.valueChanges,
        ].filter(Boolean)
      ).subscribe(() => {
        this.updateSubtotal();
      })
    );

    this.pricingSubscription.add(
      this.orderForm.get('status').valueChanges.subscribe((status) => {
        this.syncDateReceivedFromStatus(status);
      })
    );
    this.syncDateReceivedFromStatus(this.orderForm.get('status')?.value);

    const etaControl = this.orderForm.get('eta');
    if (etaControl) {
      this.lastEtaMs = this.etaTime(etaControl.value);
      this.pricingSubscription.add(
        etaControl.valueChanges.subscribe((value) => this.onEtaDateChange(value))
      );
    }
    this.syncProductDutyFlag();
    this.updateCustomAmount();
  }

  /** Keep product-level duty in sync (used by stack retail+duty display). */
  syncProductDutyFlag(): void {
    const frameDuty = !!this.orderForm?.get('products.0.frame.duty')?.value;
    const productDuty = this.orderForm?.get('products.0.duty');
    if (productDuty && !!productDuty.value !== frameDuty) {
      if (productDuty.disabled) {
        productDuty.enable({ emitEvent: false });
        productDuty.setValue(frameDuty, { emitEvent: false });
      } else {
        productDuty.setValue(frameDuty, { emitEvent: false });
      }
    }
  }

  syncDateReceivedFromStatus(status: string): void {
    if (status !== 'RECEIVED') {
      return;
    }
    const dateReceivedControl = this.orderForm?.get('products.0.frame.dateReceived');
    if (!dateReceivedControl) {
      return;
    }
    if (!dateReceivedControl.value) {
      dateReceivedControl.setValue(new Date());
    }
  }

  setupSizeToMeasurementSync(): void {
    const sizeControl = this.orderForm?.get('products.0.product.size');
    if (!sizeControl) {
      return;
    }

    this.pricingSubscription.add(
      sizeControl.valueChanges.subscribe((size) => {
        this.syncMeasurementAFromSize(size);
      })
    );

    if (sizeControl.value) {
      this.syncMeasurementAFromSize(sizeControl.value);
    }
  }

  syncMeasurementAFromSize(size: string | number | null | undefined): void {
    if (size == null || size === '') {
      return;
    }

    const sizeStr = String(size).trim();
    if (!sizeStr) {
      return;
    }

    const aControl = this.orderForm?.get('products.0.frame.measurements.a');
    if (!aControl) {
      return;
    }

    const eyeSize = sizeStr.split(/[-\s/]/)[0].replace(/\D/g, '').slice(0, 2);
    if (eyeSize) {
      aControl.setValue(eyeSize, { emitEvent: false });
    }
  }

  onMeasurementInput(event: Event, field: 'a' | 'b' | 'ed' | 'dbl'): void {
    const input = event.target as HTMLInputElement;
    const digits = (input.value || '').replace(/\D/g, '').slice(0, 2);
    if (input.value !== digits) {
      input.value = digits;
    }
    const control = this.orderForm?.get(`products.0.frame.measurements.${field}`);
    if (control && control.value !== digits) {
      control.setValue(digits, { emitEvent: false });
    }
  }

  onProductFieldSelected(query: ProductQuery): void {
    if (query?.size?.value != null && query.size.value !== '') {
      this.syncMeasurementAFromSize(query.size.value);
    }
    this.updateProduct(query);
  }

  get frameImages(): string[] {
    const images =
      this.orderForm?.get('products.0.product.images')?.value ??
      this.selectedProduct?.images ??
      [];
    return Array.isArray(images) ? images : [];
  }

  get selectedProductId(): string {
    return (
      this.orderForm?.get('products.0.product._id')?.value ||
      this.selectedProduct?._id ||
      ''
    );
  }

  onFrameImageAdd(imageId: string): void {
    if (!imageId) {
      return;
    }
    const imageList = [...this.frameImages, imageId];
    this.persistFrameImages(imageList);
  }

  onFrameImageDelete(replacementImageId?: any): void {
    if (replacementImageId && replacementImageId !== true) {
      this.persistFrameImages([replacementImageId]);
      return;
    }
    this.persistFrameImages([]);
  }

  onFrameImageReplace(imageId: string): void {
    if (!imageId) {
      return;
    }
    this.persistFrameImages([imageId]);
  }

  private persistFrameImages(imageList: string[]): void {
    const imagesControl = this.orderForm?.get('products.0.product.images');
    imagesControl?.setValue(imageList);
    if (this.selectedProduct) {
      this.selectedProduct.images = imageList;
    }

    const productId = this.selectedProductId;
    if (productId && productId !== 'NEW') {
      this.productService.updateImages(productId, imageList).subscribe((product) => {
        const savedImages = product?.images ?? imageList;
        imagesControl?.setValue(savedImages);
        if (this.selectedProduct) {
          this.selectedProduct.images = savedImages;
        }
        this.cdRef.detectChanges();
      });
    }
    this.cdRef.detectChanges();
  }

  syncEnclosedFromInventory(product: Product): void {
    if (this.isPofSelected) {
      return;
    }
    if (!product || !(product.quantity > 0)) {
      return;
    }
    const statusControl = this.orderForm?.get('products.0.frame.preOrderStatus');
    if (statusControl && statusControl.value !== 'ENCLOSED') {
      statusControl.setValue('ENCLOSED');
    }
    const physicalStatusControl = this.orderForm?.get(
      'products.0.frame.physicalStatus'
    );
    if (physicalStatusControl && physicalStatusControl.value !== 'ENCLOSED') {
      physicalStatusControl.setValue('ENCLOSED');
    }
  }

  parseCurrency(value: any): number {
    if (value == null || value === '') {
      return 0;
    }
    const parsed = parseFloat(String(value).replace(/,/g, ''));
    return isNaN(parsed) ? 0 : parsed;
  }

  syncMaterialPrice(label: string | null | undefined): void {
    const materialPriceControl = this.orderForm?.get('products.0.frame.materialPrice');
    if (!materialPriceControl) {
      return;
    }
    const selected = String(label || '').trim();
    if (!selected) {
      materialPriceControl.setValue(null, { emitEvent: false });
      return;
    }
    const match = this.settingsService.materials.find((item) => item.label === selected);
    if (match) {
      materialPriceControl.setValue(match.price ?? 0, { emitEvent: false });
      return;
    }
    if (materialPriceControl.value == null || materialPriceControl.value === '') {
      materialPriceControl.setValue(0, { emitEvent: false });
    }
  }

  ensureSelectedMaterialOption(): void {
    const materialControl = this.orderForm?.get('products.0.frame.material');
    const selected = String(materialControl?.value || '').trim();
    if (!selected) {
      return;
    }
    const exists = this.materialOptions.some((option) => option.label === selected);
    if (!exists) {
      const storedPrice = this.parseCurrency(
        this.orderForm?.get('products.0.frame.materialPrice')?.value
      );
      this.materialOptions = [
        ...this.materialOptions,
        { label: selected, price: storedPrice },
      ];
    }
  }

  updateCustomAmount(): void {
    const product = this.orderForm?.get('products.0');
    const customControl = product?.get('custom');
    if (!customControl) {
      return;
    }

    const materialLabel = String(
      this.orderForm.get('products.0.frame.material')?.value || ''
    ).trim();
    const materialPrice = materialLabel
      ? this.orderForm.get('products.0.frame.materialPrice')?.value
      : null;

    let total = this.settingsService.getCustomAddOnTotal({
      duty: this.orderForm.get('products.0.frame.duty')?.value,
      nosepadInstall: this.orderForm.get('products.0.frame.nosepadInstall')?.value,
      materialPrice,
    });

    if (this.orderForm.get('products.0.frame.partOther')?.value) {
      total += this.parseCurrency(
        this.orderForm.get('products.0.frame.partOtherPrice')?.value
      );
    }

    if (
      this.orderForm.get('products.0.frame.physicalStatus')?.value ===
      'ON-APPROVAL'
    ) {
      total += FrameDetailsComponent.ON_APPROVAL_SHIPPING;
    }

    customControl.setValue(this.formatMoney(total), { emitEvent: false });
    this.updateSubtotal();
  }

  private syncPofPhysicalStatus(pof: boolean): void {
    const physical = this.orderForm?.get('products.0.frame.physicalStatus');
    if (!physical) {
      return;
    }
    const current = String(physical.value || '');
    if (pof && current && !['ENCLOSED', 'CUT & EDGE'].includes(current)) {
      physical.setValue('', { emitEvent: false });
    }
  }

  updateSubtotal(): void {
    const product = this.orderForm?.get('products.0');
    const subtotalControl = product?.get('subtotal');
    if (!subtotalControl) {
      return;
    }

    let subtotal =
      this.parseCurrency(product.get('tax')?.value) +
      this.parseCurrency(product.get('retail')?.value) +
      this.parseCurrency(product.get('custom')?.value);
    if (this.isShipFulfillment) {
      subtotal += this.parseCurrency(product.get('shipping')?.value);
    }

    subtotalControl.setValue(this.formatMoney(subtotal), { emitEvent: false });
  }

  syncPofFrameCost(pof: boolean): void {
    const retailControl = this.orderForm?.get('products.0.retail');
    if (!retailControl) {
      return;
    }

    if (pof) {
      const currentRetail = this.parseCurrency(retailControl.value);
      if (currentRetail > 0) {
        this.savedFrameRetail = currentRetail;
      } else if (this.savedFrameRetail == null && this.selectedProduct?.retail != null) {
        this.savedFrameRetail = this.selectedProduct.retail;
      }
      retailControl.setValue(this.formatMoney(0), { emitEvent: false });
    } else if (this.savedFrameRetail != null) {
      retailControl.setValue(this.formatMoney(this.savedFrameRetail), {
        emitEvent: false,
      });
      this.savedFrameRetail = null;
      this.syncEnclosedFromInventory(this.selectedProduct);
    } else if (this.selectedProduct?.retail != null) {
      retailControl.setValue(this.formatMoney(this.selectedProduct.retail), {
        emitEvent: false,
      });
      this.syncEnclosedFromInventory(this.selectedProduct);
    }

    this.updateSubtotal();
  }

  get isPofSelected(): boolean {
    return !!this.orderForm?.get('products.0.frame.pof')?.value;
  }

  get isRepairJob(): boolean {
    const status = String(this.orderForm?.get('status')?.value || '').trim();
    return (
      status === 'TO REPAIR' ||
      status === 'AWAITING REPAIR' ||
      this.isJobActionChecked('REPAIR') ||
      this.selectedRepairItems.length > 0
    );
  }

  get repairItemOptions(): readonly string[] {
    return REPAIR_ITEM_OPTIONS;
  }

  get selectedRepairItems(): string[] {
    const value = this.orderForm?.get('products.0.frame.repairItems')?.value;
    return Array.isArray(value) ? value : [];
  }

  isRepairItemSelected(item: string): boolean {
    return this.selectedRepairItems.includes(item);
  }

  toggleRepairItem(item: string, checked: boolean): void {
    const control = this.orderForm?.get('products.0.frame.repairItems');
    if (!control) {
      return;
    }
    const current = this.selectedRepairItems.filter((value) => value !== item);
    if (checked) {
      current.push(item);
    }
    control.setValue(current);
    control.markAsDirty();
    this.refreshTaskActivity();
  }

  get isDispensed(): boolean {
    return this.orderForm?.get('status')?.value === 'DISPENSED';
  }

  get isReturned(): boolean {
    return this.orderForm?.get('status')?.value === 'RETURNED';
  }

  get isLockedCompleted(): boolean {
    return this.isDispensed || this.isReturned;
  }

  get exchangeCredit(): number {
    return this.parseCurrency(this.orderForm?.get('exchangeCredit')?.value);
  }

  get exchangeFrameRetail(): number {
    return this.parseCurrency(
      this.orderForm?.get('products.0.retail')?.value
    );
  }

  get exchangeDifferenceNote(): string {
    const credit = this.exchangeCredit;
    if (credit <= 0) {
      return '';
    }
    const retail = this.exchangeFrameRetail;
    if (retail <= 0) {
      return `Exchange credit $${this.formatMoney(credit)}. Select the replacement frame at current pricing. Tax will be recalculated on this job.`;
    }
    if (retail > credit) {
      return `Additional frame amount due $${this.formatMoney(retail - credit)} after $${this.formatMoney(credit)} exchange credit. Tax will be recalculated on this job.`;
    }
    if (credit > retail) {
      return `Customer credit $${this.formatMoney(credit - retail)} after current frame price $${this.formatMoney(retail)}. Tax will be recalculated on this job.`;
    }
    return `Replacement frame matches the $${this.formatMoney(credit)} exchange credit. Tax will be recalculated on this job.`;
  }

  get returnCreditNote(): string {
    const credit = this.parseCurrency(this.orderForm?.get('returnCredit')?.value);
    if (credit <= 0) {
      return '';
    }
    const fee = this.parseCurrency(this.orderForm?.get('restockingFee')?.value);
    const waived = !!this.orderForm?.get('restockingFeeWaived')?.value;
    const linked = String(this.orderForm?.get('linkedInvoice')?.value || '').trim();
    const from = linked ? ` from ${linked}` : '';
    if (waived || fee <= 0) {
      return `Return processed${from}. Restocking fee waived. Customer credit $${this.formatMoney(credit)}.`;
    }
    return `Return processed${from}. Restocking fee $${this.formatMoney(fee)}. Customer credit $${this.formatMoney(credit)}.`;
  }

  get frameOnlyTaxCreditNote(): string {
    const credit = this.parseCurrency(
      this.orderForm?.get('frameOnlyTaxCredit')?.value
    );
    if (credit <= 0) {
      return '';
    }
    const linked = String(
      this.orderForm?.get('linkedInvoice')?.value || ''
    ).trim();
    return `Previous Frame Only sales tax credited $${this.formatMoney(credit)}${
      linked ? ` from ${linked}` : ''
    }. This credit is one-time.`;
  }

  get redoPaidNote(): string {
    const reason = String(this.orderForm?.get('redoReason')?.value || '').trim();
    const paid = this.parseCurrency(this.orderForm?.get('redoPaidCredit')?.value);
    if (!reason && paid <= 0) {
      return '';
    }
    const current = this.parseCurrency(
      this.orderForm?.get('products.0.subtotal')?.value
    );
    const additional = Math.max(0, current - paid);
    if (additional > 0) {
      return `Original job paid $${this.formatMoney(paid)}. Additional amount due $${this.formatMoney(additional)}.`;
    }
    return `Original job paid $${this.formatMoney(paid)}. No additional amount due.`;
  }

  get physicalStatusChoices(): string[] {
    if (this.isPofSelected) {
      return ['ENCLOSED', 'CUT & EDGE'];
    }
    return this.physicalStatusOptions;
  }

  get isExistingQuote(): boolean {
    const id = String(this.orderForm?.get('_id')?.value || '').trim();
    const saved = !!id && id !== 'NEW';
    const status = String(this.orderForm?.get('status')?.value || '').trim();
    const todo = String(this.orderForm?.get('todo')?.value || '').trim();
    return saved && (status === 'QUOTE' || todo === 'QUOTE');
  }

  get jobActionOptions(): string[] {
    return this.isExistingQuote
      ? [...this.baseJobActionOptions, 'CONVERT TO ORDER']
      : [...this.baseJobActionOptions, 'QUOTE'];
  }

  initSelectedProductFromForm(): void {
    const product = this.orderForm?.get('products.0.product')?.value;
    if (product?._id) {
      this.selectedProduct = product;
    }
  }

  updateProduct(query: ProductQuery) {
    if (query.model.selected && query.color.selected && query.size.selected) {
      let productElements: FormArray = this.orderForm.get('products') as FormArray;
      let productElement: FormGroup = productElements.at(0) as FormGroup;
      let product: FormGroup = productElement.get('product') as FormGroup;
      let vendor: Vendor = product.value.vendor;
      if (!vendor?._id) {
        return;
      }
      this.productService
        .queryVendorProductsFields(vendor._id, query)
        .subscribe((res) => {
          if (!res?.[0]) {
            return;
          }
          delete res[0]?.__v;
          this.selectedProduct = res[0] as Product;
          let productFormGroup = OrderForm.initProductFormGroup(this.selectedProduct);
          productElement.setControl('product', productFormGroup);

          productElement.get('pricingFormula').setValue(this.selectedProduct.pricingFormula);
          productElement.get('type').setValue(this.selectedProduct.type);
          productElement.get('cost').setValue(this.selectedProduct.cost);

          if (this.isPofSelected) {
            // POF: frame details are informational only; inventory is ignored.
            this.savedFrameRetail = this.selectedProduct.retail;
            productElement.get('retail').setValue(this.formatMoney(0));
          } else {
            productElement.get('retail').setValue(this.selectedProduct.retail);
            this.formatCurrency(null, productElement.get('retail'));
            this.syncEnclosedFromInventory(this.selectedProduct);
          }
          this.updateSubtotal();

          if (this.selectedProduct.type == 'RX' && this.orderForm.get('type').value != 'F') {
            this.orderForm.get('type').setValue('O');
          } else if (
            this.selectedProduct.type == 'SUN' &&
            this.orderForm.get('type').value != 'F'
          ) {
            this.orderForm.get('type').setValue('N');
          }

          if (this.selectedProduct.size) {
            this.syncMeasurementAFromSize(this.selectedProduct.size);
          }
          this.cdRef.detectChanges();
        });
    }
  }

  formatPricingFields(): void {
    const product = this.orderForm?.get('products.0');
    if (!product) {
      return;
    }
    ['tax', 'retail', 'custom', 'shipping', 'subtotal'].forEach((field) => {
      this.formatCurrency(null, product.get(field));
    });
    this.formatCurrency(null, this.orderForm.get('products.0.frame.partOtherPrice'));
  }

  formatMoney(value: any): string {
    const amount = this.parseCurrency(value);
    // Always two decimal places; zero is "0.00"
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  formatCurrency($event, control) {
    if (!control) {
      return;
    }
    control.setValue(this.formatMoney(control.value), { emitEvent: false });
  }

  onSave(destination: string): void {
    const dispensedOptions = [
      'DUPLICATE',
      'EXCHANGE',
      'NEW RX',
      'RE-DO',
      'REPAIR',
      'RETURN',
      'PRINT / EMAIL',
      'PRINT',
      'EMAIL',
      'DELETE',
    ];
    if (dispensedOptions.includes(destination)) {
      this.saveOrder.emit({ destination });
      return;
    }

    this.ensureInitialsFilled();
    this.appendFrameNote();
    this.saveOrder.emit({ destination: this.resolveDestination(destination) });
  }

  onTaskWorkflowSave(destination: string): void {
    this.ensureInitialsFilled();
    this.saveOrder.emit({ destination: destination || 'SAVE' });
  }

  private refreshTaskActivity(): void {
    this.taskActivity?.normalizeAndRebuild();
  }

  onPhysicalStatusChange(value: string): void {
    this.applyPhysicalStatus(value);
    this.updateCustomAmount();
  }

  onPhysicalStatusClick(option: string, event: MouseEvent): void {
    const control = this.orderForm?.get('products.0.frame.physicalStatus');
    if (control?.value === option) {
      event.preventDefault();
      event.stopPropagation();
      control.setValue('');
      this.applyPhysicalStatus('');
      this.updateCustomAmount();
    }
  }

  onJobActionChange(value: string): void {
    this.onJobActionCheck(value, true);
  }

  isJobActionChecked(option: string): boolean {
    return hasJobAction(
      this.orderForm?.get('jobAction')?.value,
      this.orderForm?.get('jobActions')?.value,
      option
    );
  }

  onJobActionCheck(option: string, checked: boolean): void {
    if (this.isLockedCompleted) {
      return;
    }
    if (!checked) {
      this.setJobActions(toggleJobAction(this.currentJobActions, option, false));
      this.syncOpenTasksFromJobActions();
      this.refreshTaskActivity();
      if (option === ORDER_LENSES_ACTION) {
        this.applyOrderLenses(false);
      }
      return;
    }
    if (option === 'REPAIR') {
      this.promptRepairItems();
      return;
    }
    if (option === 'INQUIRY' || option === 'HOLD') {
      this.promptGenericNotes(option);
      return;
    }
    if (option === 'HOLD / RX') {
      this.promptHoldRx();
      return;
    }
    if (option === 'CALL FOR RX') {
      this.promptCallRx();
      return;
    }
    this.setJobActions(toggleJobAction(this.currentJobActions, option, true));
    this.syncOpenTasksFromJobActions();
    this.refreshTaskActivity();
    if (option === ORDER_LENSES_ACTION) {
      this.applyOrderLenses(true);
      return;
    }
    this.applyJobAction(option);
  }

  onFrameOnlyChange(checked: boolean): void {
    if (this.isReturned) {
      return;
    }
    if (this.isDispensed) {
      this.orderForm?.get('frameOnly')?.setValue(true, { emitEvent: false });
      return;
    }
    this.orderForm?.get('frameOnly')?.setValue(!!checked);
  }

  private ensureFrameStatusControls(): void {
    if (!this.orderForm) {
      return;
    }
    const frame = this.orderForm.get('products.0.frame') as FormGroup;
    if (frame && !frame.get('physicalStatus')) {
      frame.addControl('physicalStatus', this.fb.control(''));
    } else if (frame?.get('physicalStatus') && !frame.get('physicalStatus').value) {
      const savedId = String(this.orderForm.get('_id')?.value || '').trim();
      if (savedId && savedId !== 'NEW') {
        const derived = this.derivePhysicalStatus();
        if (derived) {
          frame.get('physicalStatus').setValue(derived, { emitEvent: false });
        }
      }
    }
    if (!this.orderForm.get('jobAction')) {
      this.orderForm.addControl(
        'jobAction',
        this.fb.control(this.deriveJobAction())
      );
    } else if (!this.orderForm.get('jobAction').value) {
      this.orderForm.get('jobAction').setValue(this.deriveJobAction(), {
        emitEvent: false,
      });
    }
    this.ensureJobActionsControl();
    this.ensureActivityControls(frame);
    this.ensureRepairItemsControl(frame);
    this.ensureFulfillmentControls();
    this.ensureFrameOnlyControl();
  }

  private ensureJobActionsControl(): void {
    if (!this.orderForm) {
      return;
    }
    const derived = this.deriveJobAction();
    const existing = jobActionList(
      this.orderForm.get('jobAction')?.value,
      this.orderForm.get('jobActions')?.value
    );
    if (!this.orderForm.get('jobActions')) {
      this.orderForm.addControl(
        'jobActions',
        this.fb.control(existing.length ? existing : derived ? [derived] : [])
      );
    } else if (!existing.length && derived) {
      this.orderForm.get('jobActions').setValue([derived], { emitEvent: false });
    }
    if (!this.orderForm.get('lensStatus')) {
      this.orderForm.addControl(
        'lensStatus',
        this.fb.control(this.orderForm.get('lensStatus')?.value || '')
      );
    }
  }

  private ensureFrameOnlyControl(): void {
    if (!this.orderForm) {
      return;
    }
    if (!this.orderForm.get('frameOnly')) {
      this.orderForm.addControl(
        'frameOnly',
        this.fb.control(this.deriveFrameOnly())
      );
    }
    const jobAction = String(this.orderForm.get('jobAction')?.value || '');
    if (jobAction === 'FRAME ONLY') {
      this.orderForm.get('frameOnly')?.setValue(true, { emitEvent: false });
      this.orderForm.get('jobAction')?.setValue(this.deriveJobAction(), {
        emitEvent: false,
      });
    }
    const frameOnly = this.orderForm.get('frameOnly');
    if (this.isLockedCompleted) {
      frameOnly?.disable({ emitEvent: false });
    } else {
      frameOnly?.enable({ emitEvent: false });
    }
  }

  private ensureRepairItemsControl(frame: FormGroup): void {
    if (!frame) {
      return;
    }
    if (!frame.get('repairItems')) {
      frame.addControl('repairItems', this.fb.control([]));
    }
  }

  private promptRepairItems(preselected?: string[]): void {
    const selected = normalizeRepairItems(
      preselected?.length ? preselected : this.selectedRepairItems
    );
    const dialogRef = this.dialog.open(RepairOrderDialogComponent, {
      width: '420px',
      data: {
        selected,
        repairLocation:
          this.orderForm?.get('products.0.frame.labVendor')?.value ||
          IN_HOUSE_REPAIR_LOCATION,
        repairLocations: this.settingsService.activeRepairLocations(),
      },
    });
    dialogRef.afterClosed().subscribe((result: RepairPopupResult | string[] | null) => {
      const popup = this.normalizeRepairPopup(result);
      if (!popup?.repairItems?.length) {
        const current = String(this.orderForm?.get('jobAction')?.value || '');
        if (current === 'REPAIR' && !this.selectedRepairItems.length) {
          this.orderForm?.get('jobAction')?.setValue('', { emitEvent: false });
        }
        if (preselected?.includes(NOSEPAD_INSTALL_ITEM)) {
          this.orderForm?.get('products.0.frame.nosepadInstall')?.setValue(false, {
            emitEvent: false,
          });
        }
        if (preselected?.includes(OTHER_REPAIR_ITEM)) {
          this.orderForm?.get('products.0.frame.partOther')?.setValue(false, {
            emitEvent: false,
          });
        }
        this.cdRef.detectChanges();
        return;
      }
      this.applyRepairPopup(popup);
      this.setJobActions(toggleJobAction(this.currentJobActions, 'REPAIR', true));
      this.syncOpenTasksFromJobActions();
      this.applyRepairTaskDetails(popup);
      this.applyJobAction('REPAIR');
      this.updateCustomAmount();
      this.refreshTaskActivity();
      this.cdRef.detectChanges();
    });
  }

  private promptRepairForSelection(preselected: string[]): void {
    this.promptRepairItems(preselected);
  }

  private normalizeRepairPopup(
    result: RepairPopupResult | string[] | null
  ): RepairPopupResult | null {
    if (!result) {
      return null;
    }
    if (Array.isArray(result)) {
      return {
        repairItems: normalizeRepairItems(result),
        repairLocation: IN_HOUSE_REPAIR_LOCATION,
        notes: '',
        estimate: '',
        estimateApprovalRequired: false,
        otherText: '',
      };
    }
    return {
      ...result,
      repairItems: normalizeRepairItems(result.repairItems),
    };
  }

  private applyRepairPopup(popup: RepairPopupResult): void {
    this.orderForm?.get('products.0.frame.repairItems')?.setValue(popup.repairItems);
    this.orderForm?.get('products.0.frame.repairItems')?.markAsDirty();
    this.orderForm?.get('products.0.frame.nosepadInstall')?.setValue(
      popup.repairItems.includes(NOSEPAD_INSTALL_ITEM),
      { emitEvent: false }
    );
    this.orderForm?.get('products.0.frame.partOther')?.setValue(
      popup.repairItems.includes(OTHER_REPAIR_ITEM),
      { emitEvent: false }
    );
    if (popup.repairLocation) {
      this.orderForm?.get('products.0.frame.labVendor')?.setValue(popup.repairLocation);
    }
    if (popup.otherText) {
      this.orderForm
        ?.get('products.0.frame.partOtherDescription')
        ?.setValue(popup.otherText);
    }
  }

  private applyRepairTaskDetails(popup: RepairPopupResult): void {
    const activity = this.activityFormArray;
    if (!activity) {
      return;
    }
    const existing = activity.controls.find(
      (control) =>
        String(control.get('status')?.value || '') === 'REPAIR' &&
        !control.get('completed')?.value
    ) as FormGroup;
    const extra = {
      kind: 'open',
      notes: popup.notes,
      repairItems: popup.repairItems,
      repairLocation: popup.repairLocation,
      estimate: popup.estimate,
      estimateApprovalRequired: popup.estimateApprovalRequired,
      otherText: popup.otherText,
      description: popup.otherText,
    };
    if (existing) {
      Object.keys(extra).forEach((key) => {
        if (existing.get(key)) {
          existing.get(key).setValue((extra as any)[key]);
        } else {
          existing.addControl(key, this.fb.control((extra as any)[key]));
        }
      });
      return;
    }
    activity.push(this.activityEntryGroup('REPAIR', new Date(), extra));
  }

  private removeRepairItems(items: string[]): void {
    const control = this.orderForm?.get('products.0.frame.repairItems');
    if (!control) {
      return;
    }
    const next = this.selectedRepairItems.filter((item) => !items.includes(item));
    control.setValue(next);
  }

  private promptGenericNotes(option: string): void {
    const dialogRef = this.dialog.open(GenericNotesDialogComponent, {
      width: '420px',
      data: {
        title: 'NOTES',
        message: `These notes are saved on the ${option} task.`,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result == null) {
        this.cdRef.detectChanges();
        return;
      }
      this.setJobActions(toggleJobAction(this.currentJobActions, option, true));
      this.syncOpenTasksFromJobActions();
      this.applyTaskNotes(option === 'HOLD' ? 'HOLD' : 'INQUIRY', result.notes || '');
      this.applyJobAction(option);
      this.refreshTaskActivity();
      this.cdRef.detectChanges();
    });
  }

  private promptHoldRx(): void {
    const dialogRef = this.dialog.open(HoldFollowUpDialogComponent, {
      width: '420px',
      data: {
        followUpDate: this.orderForm?.get('products.0.frame.followUpDate')?.value,
      },
    });
    dialogRef.afterClosed().subscribe((result: HoldRxPopupResult | Date | null) => {
      const popup =
        result instanceof Date
          ? {
              notes: '',
              expectedDate: result,
              followUpDate: result,
            }
          : result;
      if (!popup) {
        this.cdRef.detectChanges();
        return;
      }
      this.orderForm
        ?.get('products.0.frame.followUpDate')
        ?.setValue(popup.followUpDate);
      this.setJobActions(toggleJobAction(this.currentJobActions, 'HOLD / RX', true));
      this.syncOpenTasksFromJobActions();
      this.applyTaskNotes('HOLD / RX', popup.notes || '', {
        expectedDate: popup.expectedDate,
        followUpDate: popup.followUpDate,
      });
      this.applyJobAction('HOLD / RX');
      this.refreshTaskActivity();
      this.cdRef.detectChanges();
    });
  }

  private promptCallRx(): void {
    const dialogRef = this.dialog.open(CallRxDialogComponent, {
      width: '420px',
      data: {
        whoToCallOptions: this.settingsService.activeWhoToCallOptions(),
        date: this.orderForm?.get('products.0.frame.followUpDate')?.value || new Date(),
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        this.cdRef.detectChanges();
        return;
      }
      if (result.date) {
        this.orderForm?.get('products.0.frame.followUpDate')?.setValue(result.date);
      }
      this.setJobActions(toggleJobAction(this.currentJobActions, 'CALL FOR RX', true));
      this.syncOpenTasksFromJobActions();
      this.applyTaskNotes('CALL / RX', result.notes || '', {
        whoToCall: result.whoToCall,
        followUpDate: result.date,
        waitingOn: result.whoToCall,
      });
      this.applyJobAction('CALL FOR RX');
      this.refreshTaskActivity();
      this.cdRef.detectChanges();
    });
  }

  private applyTaskNotes(
    status: string,
    notes: string,
    extra: Record<string, any> = {}
  ): void {
    const activity = this.activityFormArray;
    if (!activity) {
      return;
    }
    const existing = activity.controls.find((control) => {
      const value = String(control.get('status')?.value || '');
      return (
        !control.get('completed')?.value &&
        (value === status ||
          (status === 'CALL / RX' && (value === 'CALL FOR RX' || value === 'CALL / RX')))
      );
    }) as FormGroup;
    if (!existing) {
      activity.push(this.activityEntryGroup(status, new Date(), { notes, ...extra }));
      return;
    }
    if (existing.get('notes')) {
      existing.get('notes').setValue(notes);
    }
    Object.keys(extra).forEach((key) => {
      if (existing.get(key)) {
        existing.get(key).setValue(extra[key]);
      } else {
        existing.addControl(key, this.fb.control(extra[key]));
      }
    });
  }

  private ensureFulfillmentControls(): void {
    if (!this.orderForm) {
      return;
    }
    if (!this.orderForm.get('fulfillment')) {
      this.orderForm.addControl('fulfillment', this.fb.control('PICKUP'));
    } else if (!this.orderForm.get('fulfillment').value) {
      this.orderForm.get('fulfillment').setValue('PICKUP', { emitEvent: false });
    }
    if (!this.orderForm.get('shippingAddress')) {
      this.orderForm.addControl(
        'shippingAddress',
        this.fb.group({
          address: '',
          address2: '',
          city: '',
          state: '',
          zip: '',
        })
      );
    }
    if (this.isShipFulfillment) {
      this.seedShippingAddress();
    }
    const fulfillment = this.orderForm.get('fulfillment');
    const activityStatus = this.orderForm.get('products.0.frame.activityStatus');
    if (this.isLockedCompleted) {
      fulfillment?.disable({ emitEvent: false });
      activityStatus?.disable({ emitEvent: false });
    } else {
      fulfillment?.enable({ emitEvent: false });
      activityStatus?.enable({ emitEvent: false });
    }
  }

  private deriveFrameOnly(): boolean {
    if (this.orderForm?.get('frameOnly')?.value) {
      return true;
    }
    return String(this.orderForm?.get('jobAction')?.value || '') === 'FRAME ONLY';
  }

  private ensureActivityControls(frame: FormGroup): void {
    if (!frame) {
      return;
    }
    if (!frame.get('activity')) {
      frame.addControl('activity', this.fb.array([]));
    }
    if (!frame.get('activityStatus')) {
      frame.addControl('activityStatus', this.fb.control(''));
    }
    ['carrier', 'trackingNumber', 'shipDate', 'labVendor'].forEach((key) => {
      if (!frame.get(key)) {
        frame.addControl(key, this.fb.control(''));
      }
    });
    ['etaChecked', 'etaCheckedBy', 'etaCheckedAt'].forEach((key) => {
      if (!frame.get(key)) {
        frame.addControl(key, this.fb.control(key === 'etaChecked' ? false : ''));
      }
    });
    this.seedActivityFromStatus();
    this.backfillEtaOnOrderedActivity();
    this.syncOpenTasksFromJobActions();
    this.syncEtaCheckTask();
    const latest = this.latestActivityEntry;
    const current =
      String(frame.get('activityStatus')?.value || '').trim() ||
      latest?.status ||
      this.mapStatusToActivity(this.orderForm.get('status')?.value);
    if (current && frame.get('activityStatus')?.value !== current) {
      frame.get('activityStatus').setValue(current, { emitEvent: false });
    }
  }

  private seedActivityFromStatus(): void {
    const activity = this.activityFormArray;
    if (!activity || activity.length) {
      return;
    }
    const status = String(this.orderForm.get('status')?.value || '').trim();
    const date = this.orderForm.get('date')?.value || new Date();
    const referenceNumber = String(
      this.orderForm.get('products.0.frame.referenceNumber')?.value || ''
    ).trim();
    const enclosed =
      this.orderForm.get('products.0.frame.physicalStatus')?.value === 'ENCLOSED' ||
      this.orderForm.get('products.0.frame.preOrderStatus')?.value === 'ENCLOSED';
    const warranty = !!this.orderForm.get('warranty')?.value;

    if (status === 'TO ORDER' || (status === 'ORDER FRAME' && !enclosed)) {
      activity.push(this.activityEntryGroup('ORDER FRAME', date, { kind: 'open' }));
    } else if (status === 'HOLD/RX' || status === 'HOLD / RX') {
      activity.push(this.activityEntryGroup('HOLD / RX', date));
    } else if (status === 'CALL FOR RX' || status === 'CALL / RX') {
      activity.push(this.activityEntryGroup('CALL FOR RX', date));
    } else if (status === 'VOID') {
      activity.push(this.activityEntryGroup('VOID', date));
    } else if (status === 'AWAITING REPAIR' || status === 'SENT FOR REPAIR') {
      activity.push(
        this.activityEntryGroup('SENT FOR REPAIR', date, this.etaSeedExtra(referenceNumber))
      );
    } else if (/^AWAITING /i.test(status) || status === 'ORDERED') {
      activity.push(
        this.activityEntryGroup('ORDERED', date, this.etaSeedExtra(referenceNumber))
      );
    } else if (enclosed && warranty) {
      activity.push(this.activityEntryGroup('ENCLOSED', date));
    }

    const dateReceived = this.orderForm.get('products.0.frame.dateReceived')?.value;
    if (dateReceived || status === 'RECEIVED') {
      activity.push(
        this.activityEntryGroup('RECEIVED', dateReceived || new Date())
      );
    }
    if (status === 'DISPENSED') {
      const carrier = String(
        this.orderForm.get('products.0.frame.carrier')?.value || ''
      ).trim();
      const trackingNumber = String(
        this.orderForm.get('products.0.frame.trackingNumber')?.value || ''
      ).trim();
      const shipDate =
        this.orderForm.get('products.0.frame.shipDate')?.value || new Date();
      if (carrier || trackingNumber || this.orderForm.get('fulfillment')?.value === 'SHIP') {
        activity.push(
          this.activityEntryGroup('SHIPPED', shipDate, { carrier, trackingNumber })
        );
      } else {
        activity.push(this.activityEntryGroup('DISPENSED', new Date()));
      }
    }
  }

  private activityEntryGroup(
    status: string,
    date: any,
    extra: {
      referenceNumber?: string;
      carrier?: string;
      trackingNumber?: string;
      labVendor?: string;
      eta?: any;
      completed?: boolean;
      completedBy?: string;
      completedAt?: any;
      kind?: string;
      notes?: string;
      description?: string;
      repairItems?: string[];
      followUpDate?: any;
      expectedDate?: any;
      waitingOn?: string;
      vendor?: string;
      repairLocation?: string;
      estimate?: string;
      estimateApprovalRequired?: boolean;
      otherText?: string;
      whoToCall?: string;
    } = {}
  ): FormGroup {
    return this.fb.group({
      id: '',
      kind: extra.kind || '',
      date,
      status,
      branch: '',
      notes: extra.notes || '',
      repairItems: [extra.repairItems || []],
      waitingOn: extra.waitingOn || '',
      vendor: extra.vendor || '',
      followUpDate: extra.followUpDate || '',
      expectedDate: extra.expectedDate || '',
      followUpNumber: 0,
      activityChoice: '',
      reopenedFrom: '',
      sourceId: '',
      description: extra.description || '',
      referenceNumber: extra.referenceNumber || '',
      carrier: extra.carrier || '',
      trackingNumber: extra.trackingNumber || '',
      labVendor: extra.labVendor || extra.repairLocation || '',
      repairLocation: extra.repairLocation || '',
      estimate: extra.estimate || '',
      estimateApprovalRequired: !!extra.estimateApprovalRequired,
      approvalStatus: '',
      otherText: extra.otherText || '',
      whoToCall: extra.whoToCall || '',
      images: [[]],
      eta: extra.eta || '',
      completed: !!extra.completed,
      completedBy: extra.completedBy || '',
      completedAt: extra.completedAt || '',
    });
  }

  get activityFormArray(): FormArray {
    return this.orderForm?.get('products.0.frame.activity') as FormArray;
  }

  get activityDateControl() {
    return this.orderForm?.get('date');
  }

  get latestActivityEntry(): {
    date?: any;
    status?: string;
    referenceNumber?: string;
    carrier?: string;
    trackingNumber?: string;
    labVendor?: string;
    eta?: any;
    completed?: boolean;
    completedBy?: string;
    completedAt?: any;
  } | null {
    const entries = this.activityFormArray?.getRawValue() || [];
    return entries.length ? entries[entries.length - 1] : null;
  }

  get activityHistory(): Array<{
    date?: any;
    status?: string;
    referenceNumber?: string;
    carrier?: string;
    trackingNumber?: string;
    labVendor?: string;
    eta?: any;
    completed?: boolean;
    completedBy?: string;
    completedAt?: any;
  }> {
    const entries = this.activityFormArray?.getRawValue() || [];
    return entries.slice().reverse();
  }

  get activityHistoryRows(): Array<{
    index: number;
    entry: {
      date?: any;
      status?: string;
      referenceNumber?: string;
      carrier?: string;
      trackingNumber?: string;
      labVendor?: string;
      eta?: any;
      completed?: boolean;
      completedBy?: string;
      completedAt?: any;
    };
  }> {
    const entries = this.activityFormArray?.getRawValue() || [];
    return entries
      .map((entry, index) => ({ index, entry }))
      .slice()
      .reverse();
  }

  get openTaskRows(): Array<{
    index: number;
    entry: {
      date?: any;
      status?: string;
      completed?: boolean;
      completedBy?: string;
    };
  }> {
    return this.activityHistoryRows.filter(
      (row) => isOpenTaskStatus(row.entry?.status) && !row.entry?.completed
    );
  }

  get completedActivityRows(): Array<{
    index: number;
    entry: {
      date?: any;
      status?: string;
      referenceNumber?: string;
      carrier?: string;
      trackingNumber?: string;
      labVendor?: string;
      eta?: any;
      completed?: boolean;
      completedBy?: string;
      completedAt?: any;
    };
  }> {
    return this.activityHistoryRows.filter(
      (row) => !isOpenTaskStatus(row.entry?.status) || !!row.entry?.completed
    );
  }

  taskLabel(status?: string): string {
    return taskDisplayName(status);
  }

  activityHistoryLine(entry: {
    date?: any;
    status?: string;
    referenceNumber?: string;
    carrier?: string;
    trackingNumber?: string;
    labVendor?: string;
    eta?: any;
    completedBy?: string;
  }): string {
    const date = this.activityDateDisplay(entry?.date, 'M/d/yy');
    const status = this.taskLabel(entry?.status);
    const parts = [date, status].filter(Boolean);
    if (entry?.carrier) {
      parts.push(entry.carrier);
    }
    if (entry?.trackingNumber) {
      parts.push(`Tracking #${entry.trackingNumber}`);
    }
    if (entry?.labVendor) {
      parts.push(entry.labVendor);
    }
    if (entry?.referenceNumber) {
      parts.push(`Ref. #${entry.referenceNumber}`);
    }
    if (entry?.eta) {
      parts.push(`ETA ${this.activityDateDisplay(entry.eta)}`);
    }
    if (entry?.completedBy) {
      parts.push(entry.completedBy);
    }
    return parts.join(' — ');
  }

  get activityStatusOptions(): string[] {
    const current = String(
      this.orderForm?.get('products.0.frame.activityStatus')?.value ||
        this.latestActivityEntry?.status ||
        this.mapStatusToActivity(this.orderForm?.get('status')?.value) ||
        ''
    ).trim();
    const options = this.nextActivityOptions(current);
    if (current && !options.includes(current)) {
      return [current, ...options];
    }
    return options;
  }

  get showActivityReference(): boolean {
    const activityStatus = String(
      this.orderForm?.get('products.0.frame.activityStatus')?.value || ''
    ).trim();
    return activityStatus === 'ORDERED' || activityStatus === 'SENT FOR REPAIR';
  }

  get showActivityEta(): boolean {
    return this.showActivityReference;
  }

  get etaDateControl() {
    return this.orderForm?.get('eta');
  }

  get showActivityLabVendor(): boolean {
    return (
      String(this.orderForm?.get('products.0.frame.activityStatus')?.value || '') ===
      'SENT FOR REPAIR'
    );
  }

  get showActivityShipFields(): boolean {
    return (
      String(this.orderForm?.get('products.0.frame.activityStatus')?.value || '') ===
      'SHIPPED'
    );
  }

  get showActivityFollowUp(): boolean {
    const activityStatus = String(
      this.orderForm?.get('products.0.frame.activityStatus')?.value || ''
    ).trim();
    const status = String(this.orderForm?.get('status')?.value || '').trim();
    return (
      activityStatus === 'HOLD / RX' ||
      activityStatus === 'HOLD/RX' ||
      status === 'HOLD/RX' ||
      activityStatus === 'CALL FOR RX' ||
      activityStatus === 'CALL / RX' ||
      activityStatus === 'CALLED FOR RX' ||
      status === 'CALL FOR RX'
    );
  }

  get followUpDateControl() {
    return this.orderForm?.get('products.0.frame.followUpDate');
  }

  get isShipFulfillment(): boolean {
    return String(this.orderForm?.get('fulfillment')?.value || '') === 'SHIP';
  }

  get shippingAddressGroup(): FormGroup {
    return this.orderForm?.get('shippingAddress') as FormGroup;
  }

  activityDateDisplay(value: any, format = 'M/d/yy'): string {
    if (!value) {
      return '';
    }
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) {
      return String(value);
    }
    return formatDate(date, format, this.locale);
  }

  onActivityStatusChange(value: string): void {
    if (value === 'ORDERED' || value === 'SENT FOR REPAIR') {
      this.orderForm?.get('date')?.setValue(new Date());
      this.ensureDefaultEta();
    }
    if (value === 'CALLED FOR RX') {
      this.ensureCallFollowUpDefault();
    }
    if (value === 'SHIPPED') {
      this.orderForm?.get('products.0.frame.shipDate')?.setValue(new Date());
    }
  }

  onFulfillmentChange(value: string): void {
    this.orderForm?.get('fulfillment')?.setValue(value);
    if (value === 'SHIP') {
      const shipping = this.orderForm?.get('products.0.shipping');
      if (shipping && !this.parseCurrency(shipping.value)) {
        shipping.setValue(
          this.formatMoney(FrameDetailsComponent.DEFAULT_SHIP_CHARGE),
          { emitEvent: false }
        );
      }
      this.seedShippingAddress();
    }
    this.updateSubtotal();
  }

  private seedShippingAddress(): void {
    const group = this.shippingAddressGroup;
    if (!group || !this.patient) {
      return;
    }
    if (String(group.get('address')?.value || '').trim()) {
      return;
    }
    group.patchValue({
      address: this.patient.address || '',
      address2: this.patient.address2 || '',
      city: this.patient.city || '',
      state: this.patient.state || '',
      zip: this.patient.zip || '',
    });
  }

  private mapStatusToActivity(status: string): string {
    const value = String(status || '').trim();
    if (value === 'ORDER FRAME' || value === 'TO ORDER') {
      return 'TO ORDER';
    }
    if (value === 'HOLD/RX' || value === 'HOLD / RX') {
      return 'HOLD / RX';
    }
    if (value === 'HOLD') {
      return 'HOLD';
    }
    if (value === 'CALL FOR RX' || value === 'CALL / RX') {
      return 'CALL FOR RX';
    }
    if (value === 'VOID' || value === 'CANCELED') {
      return 'VOID';
    }
    if (value === 'AWAITING REPAIR') {
      return 'SENT FOR REPAIR';
    }
    if (value === 'TO REPAIR') {
      return 'REPAIR / ENCLOSED';
    }
    if (/^AWAITING /i.test(value)) {
      return 'ORDERED';
    }
    return value;
  }

  private nextActivityOptions(current: string): string[] {
    if (current === 'HOLD / RX' || current === 'HOLD/RX') {
      return ['HOLD / RX', 'CALLED FOR RX', 'RX RECEIVED', 'VOID'];
    }
    if (current === 'HOLD') {
      return ['HOLD'];
    }
    if (current === FIRST_INSPECTION_NEEDED) {
      return [FIRST_INSPECTION_NEEDED];
    }
    if (current === 'CALL FOR RX' || current === 'CALL / RX') {
      return ['CALL FOR RX', 'CALLED FOR RX', 'RX RECEIVED', 'VOID'];
    }
    if (current === 'CALLED FOR RX') {
      return ['CALLED FOR RX', 'RX RECEIVED', 'VOID'];
    }
    if (current === 'RX RECEIVED') {
      return ['RX RECEIVED', 'TO ORDER', 'ORDERED'];
    }
    if (current === 'TO ORDER' || current === 'ORDER FRAME') {
      return ['TO ORDER', 'ORDERED'];
    }
    if (current === 'ENCLOSED') {
      return ['ENCLOSED', 'SENT FOR REPAIR'];
    }
    if (current === 'REPAIR / ENCLOSED' || current === 'TO REPAIR') {
      return ['REPAIR / ENCLOSED', 'SENT FOR REPAIR'];
    }
    if (current === 'SENT FOR REPAIR') {
      return ['SENT FOR REPAIR', 'RECEIVED'];
    }
    if (!current) {
      return [];
    }
    if (current === 'ORDERED' || (/^AWAITING /i.test(current) && current !== 'AWAITING REPAIR')) {
      const options = ['ORDERED', 'RECEIVED'];
      if (this.isJobActionChecked(ORDER_LENSES_ACTION) && this.lensStatusValue !== 'RECEIVED') {
        options.push('RECEIVED LENSES');
      }
      return options;
    }
    if (current === 'RECEIVED LENSES') {
      return this.isFrameStillAwaiting() ? ['RECEIVED LENSES', 'RECEIVED'] : ['RECEIVED LENSES'];
    }
    if (current === 'AWAITING LENSES') {
      return ['AWAITING LENSES', 'RECEIVED LENSES'];
    }
    if (current === 'RECEIVED') {
      return ['RECEIVED', 'DISPENSED', 'SHIPPED'];
    }
    if (current === 'SHIPPED') {
      return ['SHIPPED'];
    }
    if (current === 'DISPENSED' || current === 'VOID') {
      return [current];
    }
    return current ? [current] : [];
  }

  private isActivityOrderingFromStack(): boolean {
    const activityStatus = String(
      this.orderForm?.get('products.0.frame.activityStatus')?.value || ''
    ).trim();
    if (activityStatus !== 'ORDERED') {
      return false;
    }
    const physical = String(
      this.orderForm?.get('products.0.frame.physicalStatus')?.value || ''
    );
    if (physical === 'ENCLOSED') {
      return false;
    }
    const status = String(this.orderForm?.get('status')?.value || '').trim();
    if (status === 'TO ORDER' || status === 'ORDER FRAME') {
      return true;
    }
    return this.latestActivityEntry?.status === 'TO ORDER';
  }

  private isActivityVoidingHold(): boolean {
    const activityStatus = String(
      this.orderForm?.get('products.0.frame.activityStatus')?.value || ''
    ).trim();
    if (activityStatus !== 'VOID') {
      return false;
    }
    const status = String(this.orderForm?.get('status')?.value || '').trim();
    if (
      status === 'HOLD/RX' ||
      status === 'HOLD / RX' ||
      status === 'CALL FOR RX' ||
      status === 'CALL / RX'
    ) {
      return true;
    }
    const latest = this.latestActivityEntry?.status;
    return (
      this.isJobActionChecked('HOLD / RX') ||
      this.isJobActionChecked('CALL FOR RX') ||
      latest === 'HOLD / RX' ||
      latest === 'CALL FOR RX' ||
      latest === 'CALLED FOR RX'
    );
  }

  private isActivityCalledForRx(): boolean {
    const activityStatus = String(
      this.orderForm?.get('products.0.frame.activityStatus')?.value || ''
    ).trim();
    return activityStatus === 'CALLED FOR RX';
  }

  private isActivityRxReceived(): boolean {
    const activityStatus = String(
      this.orderForm?.get('products.0.frame.activityStatus')?.value || ''
    ).trim();
    return activityStatus === 'RX RECEIVED';
  }

  private isActivityReceivingFrame(): boolean {
    const activityStatus = String(
      this.orderForm?.get('products.0.frame.activityStatus')?.value || ''
    ).trim();
    if (activityStatus !== 'RECEIVED') {
      return false;
    }
    const status = String(this.orderForm?.get('status')?.value || '').trim();
    const latest = this.latestActivityEntry?.status;
    return (
      /^AWAITING /i.test(status) ||
      latest === 'ORDERED' ||
      latest === 'SENT FOR REPAIR'
    );
  }

  private isActivityReceivingLenses(): boolean {
    const activityStatus = String(
      this.orderForm?.get('products.0.frame.activityStatus')?.value || ''
    ).trim();
    if (activityStatus !== 'RECEIVED LENSES') {
      return false;
    }
    return this.lensStatusValue !== 'RECEIVED';
  }

  private isActivityDispensing(): boolean {
    const activityStatus = String(
      this.orderForm?.get('products.0.frame.activityStatus')?.value || ''
    ).trim();
    if (activityStatus !== 'DISPENSED') {
      return false;
    }
    const status = String(this.orderForm?.get('status')?.value || '').trim();
    if (status === 'DISPENSED') {
      return false;
    }
    return this.latestActivityEntry?.status !== 'DISPENSED';
  }

  private isActivityShipping(): boolean {
    const activityStatus = String(
      this.orderForm?.get('products.0.frame.activityStatus')?.value || ''
    ).trim();
    if (activityStatus !== 'SHIPPED') {
      return false;
    }
    const status = String(this.orderForm?.get('status')?.value || '').trim();
    if (status === 'DISPENSED') {
      return false;
    }
    return this.latestActivityEntry?.status !== 'SHIPPED';
  }

  private isActivitySendingRepair(): boolean {
    const activityStatus = String(
      this.orderForm?.get('products.0.frame.activityStatus')?.value || ''
    ).trim();
    if (activityStatus !== 'SENT FOR REPAIR') {
      return false;
    }
    return this.latestActivityEntry?.status !== 'SENT FOR REPAIR';
  }

  private derivePhysicalStatus(): string {
    const frame = this.orderForm?.get('products.0.frame')?.value || {};
    const status = String(this.orderForm?.get('status')?.value || '');
    const received =
      !!frame.dateReceived || status === 'RECEIVED' || status === 'DISPENSED';
    if (received) {
      return frame.physicalStatus === 'ORDERED' ? '' : frame.physicalStatus || '';
    }
    if (frame.physicalStatus) {
      return frame.physicalStatus;
    }
    if (
      frame.frameSource === 'PATIENT APPROVAL' ||
      this.orderForm?.get('todo')?.value === 'ORDERED ON APPROVAL'
    ) {
      return 'ON-APPROVAL';
    }
    const todo = String(this.orderForm?.get('todo')?.value || '');
    if (todo === 'ORDERED' || /^AWAITING /i.test(status)) {
      return 'ORDERED';
    }
    if (frame.preOrderStatus === 'ENCLOSED' || frame.enclosed) {
      return 'ENCLOSED';
    }
    if (this.isJobActionChecked('SEND TO STACK') || status === 'TO ORDER') {
      return 'TO ORDER';
    }
    return '';
  }

  private deriveJobAction(): string {
    const stored = String(this.orderForm?.get('jobAction')?.value || '').trim();
    if (stored && stored !== 'FRAME ONLY') {
      if (stored === 'CALL / RX') {
        return 'CALL FOR RX';
      }
      return stored;
    }
    const status = String(this.orderForm?.get('status')?.value || '');
    const todo = String(this.orderForm?.get('todo')?.value || '');
    const rxStatus = String(this.orderForm?.get('rxStatus')?.value || '');
    if (status === 'INQUIRY' || todo === 'INQUIRY') {
      return 'INQUIRY';
    }
    if (todo === 'QUOTE' || status === 'QUOTE') {
      return 'QUOTE';
    }
    if (
      rxStatus === 'CALL FOR RX' ||
      status === 'CALL FOR RX' ||
      todo === 'CALL'
    ) {
      return 'CALL FOR RX';
    }
    if (status === 'HOLD') {
      return 'HOLD';
    }
    if (todo === 'HOLD' || status === 'HOLD/RX' || status === 'HOLD / RX') {
      return 'HOLD / RX';
    }
    if (status === 'TO REPAIR' || status === 'AWAITING REPAIR' || todo === 'REPAIR') {
      return 'REPAIR';
    }
    if (status === 'TO ORDER' || todo === 'ORDER') {
      return 'SEND TO STACK';
    }
    return '';
  }

  private applyPhysicalStatus(value: string): void {
    const dateControl = this.orderForm?.get('date');
    const statusControl = this.orderForm?.get('status');
    const todoControl = this.orderForm?.get('todo');
    const preOrderStatus = this.orderForm?.get('products.0.frame.preOrderStatus');
    const frameSource = this.orderForm?.get('products.0.frame.frameSource');
    if (!statusControl || !todoControl) {
      return;
    }

    if (value === 'ENCLOSED') {
      preOrderStatus?.setValue('ENCLOSED');
      this.orderForm?.get('products.0.frame.enclosed')?.setValue(true);
      if (/^AWAITING /i.test(String(statusControl.value || ''))) {
        if (this.isJobActionChecked('SEND TO STACK')) {
          statusControl.setValue(this.getStackOrderStatusForType());
          todoControl.setValue('ORDER');
        } else if (this.isJobActionChecked('HOLD / RX')) {
          statusControl.setValue(this.getStackOrderStatusForType());
          todoControl.setValue('HOLD');
        } else if (this.isJobActionChecked('HOLD')) {
          statusControl.setValue('HOLD');
          todoControl.setValue('HOLD');
        } else if (this.isJobActionChecked('CALL FOR RX')) {
          statusControl.setValue('CALL FOR RX');
          todoControl.setValue('CALL');
        } else if (this.isJobActionChecked('INQUIRY')) {
          statusControl.setValue('INQUIRY');
          todoControl.setValue('INQUIRY');
        } else if (this.isJobActionChecked('QUOTE')) {
          statusControl.setValue('QUOTE');
          todoControl.setValue('QUOTE');
        } else if (this.isJobActionChecked(ORDER_LENSES_ACTION)) {
          statusControl.setValue('AWAITING LENSES');
          todoControl.setValue('ORDERED');
        } else {
          statusControl.setValue('');
        }
      }
      if (
        !this.isJobActionChecked('HOLD / RX') &&
        !this.isJobActionChecked('HOLD') &&
        !this.isJobActionChecked('CALL FOR RX') &&
        !this.isJobActionChecked('INQUIRY') &&
        !this.isJobActionChecked('QUOTE') &&
        !this.isJobActionChecked('REPAIR')
      ) {
        todoControl.setValue('ORDER');
        if (!statusControl.value) {
          statusControl.setValue(this.getStackOrderStatusForType());
        }
      }
      this.setJobActions(toggleJobAction(this.currentJobActions, 'SEND TO STACK', false));
      this.refreshTaskActivity();
      return;
    }

    if (value === 'TO ORDER') {
      preOrderStatus?.setValue('');
      this.setJobActions(toggleJobAction(this.currentJobActions, 'SEND TO STACK', true));
      if (dateControl && !dateControl.value) {
        dateControl.setValue(new Date());
      }
      todoControl.setValue('ORDER');
      frameSource?.setValue(
        frameSource.value === 'PATIENT APPROVAL' ? 'PATIENT APPROVAL' : 'PATIENT'
      );
      statusControl.setValue('TO ORDER');
      const activityStatus = this.orderForm?.get('products.0.frame.activityStatus');
      if (
        !activityStatus?.value ||
        activityStatus.value === 'TO ORDER' ||
        activityStatus.value === 'ORDER FRAME'
      ) {
        activityStatus?.setValue('TO ORDER');
      }
      this.syncOpenTasksFromJobActions();
      this.refreshTaskActivity();
      return;
    }

    if (value === '') {
      if (this.isJobActionChecked('SEND TO STACK')) {
        this.setJobActions(toggleJobAction(this.currentJobActions, 'SEND TO STACK', false));
      }
      this.removeOpenTasks(['TO ORDER', 'ORDER FRAME']);
      this.refreshTaskActivity();
      return;
    }

    if (value === 'CUT & EDGE') {
      preOrderStatus?.setValue('CUT & EDGE');
      return;
    }

    if (value === 'ORDERED' || value === 'ON-APPROVAL') {
      preOrderStatus?.setValue('');
      if (dateControl && !dateControl.value) {
        dateControl.setValue(new Date());
      }
      this.ensureDefaultEta();
      const activityStatus = this.orderForm?.get('products.0.frame.activityStatus');
      const currentActivity = String(activityStatus?.value || '').trim();
      if (
        !currentActivity ||
        currentActivity === 'TO ORDER' ||
        currentActivity === 'ORDER FRAME'
      ) {
        activityStatus?.setValue('ORDERED');
      }
      if (
        this.isJobActionChecked('QUOTE') ||
        this.isJobActionChecked('CONVERT TO ORDER') ||
        this.isJobActionChecked('CALL FOR RX') ||
        this.isJobActionChecked('HOLD / RX')
      ) {
        frameSource?.setValue(
          value === 'ON-APPROVAL' ? 'PATIENT APPROVAL' : 'PATIENT'
        );
        return;
      }
      statusControl.setValue(this.getAwaitingStatusForType());
      todoControl.setValue(value === 'ON-APPROVAL' ? 'ORDERED ON APPROVAL' : 'ORDERED');
      frameSource?.setValue(
        value === 'ON-APPROVAL' ? 'PATIENT APPROVAL' : 'PATIENT'
      );
    }
  }

  private applyJobAction(value: string): void {
    const rxStatus = this.orderForm?.get('rxStatus');
    if (value === 'CALL FOR RX' || value === 'CALL / RX') {
      rxStatus?.setValue('CALL FOR RX');
      this.ensureCallFollowUpDefault();
      const status = String(this.orderForm?.get('status')?.value || '').trim();
      if (
        !status ||
        status === 'QUOTE' ||
        status === 'INQUIRY' ||
        status === 'HOLD/RX' ||
        status === 'HOLD / RX'
      ) {
        this.orderForm?.get('status')?.setValue('CALL FOR RX');
        const activityStatus = this.orderForm?.get('products.0.frame.activityStatus');
        if (activityStatus && !activityStatus.value) {
          activityStatus.setValue('CALL FOR RX');
        }
      }
      return;
    }
    if (rxStatus?.value === 'CALL FOR RX' && !this.isJobActionChecked('CALL FOR RX')) {
      rxStatus.setValue('');
    }
    if (value === 'HOLD / RX') {
      this.ensureHoldFollowUpDefault();
      const activityStatus = this.orderForm?.get('products.0.frame.activityStatus');
      if (activityStatus && !activityStatus.value) {
        activityStatus.setValue('HOLD / RX');
      }
    }
    if (value === 'HOLD') {
      const status = String(this.orderForm?.get('status')?.value || '').trim();
      if (
        !status ||
        status === 'QUOTE' ||
        status === 'INQUIRY' ||
        status === 'HOLD/RX' ||
        status === 'HOLD / RX' ||
        status === 'CALL FOR RX'
      ) {
        this.orderForm?.get('status')?.setValue('HOLD');
      }
      this.orderForm?.get('todo')?.setValue('HOLD');
      const activityStatus = this.orderForm?.get('products.0.frame.activityStatus');
      if (activityStatus && !activityStatus.value) {
        activityStatus.setValue('HOLD');
      }
    }
    if (value === 'QUOTE') {
      this.orderForm?.get('status')?.setValue('QUOTE');
    }
    if (value === 'REPAIR') {
      const pof = this.orderForm?.get('products.0.frame.pof');
      const physical = this.orderForm?.get('products.0.frame.physicalStatus');
      const enclosed = this.orderForm?.get('products.0.frame.enclosed');
      pof?.setValue(true);
      physical?.setValue('ENCLOSED');
      enclosed?.setValue(true);
      const activityStatus = this.orderForm?.get('products.0.frame.activityStatus');
      if (activityStatus && !activityStatus.value) {
        activityStatus.setValue('REPAIR / ENCLOSED');
      }
    }
  }

  private ensureHoldFollowUpDefault(): void {
    const followUp = this.orderForm?.get('products.0.frame.followUpDate');
    if (!followUp) {
      return;
    }
    const current = followUp.value;
    if (current) {
      const parsed = current instanceof Date ? current : new Date(current);
      if (!isNaN(parsed.getTime())) {
        return;
      }
    }
    followUp.setValue(new Date(Date.now() + 48 * 60 * 60 * 1000));
  }

  private ensureCallFollowUpDefault(): void {
    const followUp = this.orderForm?.get('products.0.frame.followUpDate');
    if (!followUp) {
      return;
    }
    const current = followUp.value;
    if (current) {
      const parsed = current instanceof Date ? current : new Date(current);
      if (!isNaN(parsed.getTime())) {
        return;
      }
    }
    followUp.setValue(new Date());
  }

  resolveDestination(explicit: string): string {
    const reserved = [
      'VOID',
      'ADD TO CART',
      'RETURN',
      'RE-STYLE',
      'NEW RX',
      'DUPLICATE',
      'EXCHANGE',
      'RE-DO',
      'REPAIR',
      'TO REPAIR',
      'PRINT / EMAIL',
      'PRINT',
      'EMAIL',
      'DELETE',
      'ADDED TO INVENTORY',
    ];
    if (reserved.includes(explicit)) {
      return explicit;
    }

    if (this.isActivityVoidingHold()) {
      return 'VOID';
    }

    if (this.isActivityRxReceived()) {
      return 'RX RECEIVED';
    }
    if (this.isActivityCalledForRx()) {
      return 'CALLED FOR RX';
    }

    const job = exclusiveJobAction(this.currentJobActions);
    if (job === 'HOLD / RX') {
      return 'SAVE/HOLD';
    }
    if (job === 'HOLD') {
      return 'HOLD';
    }
    if (job === 'QUOTE') {
      return 'SAVE/QUOTE';
    }
    if (job === 'CONVERT TO ORDER') {
      return 'CONVERT TO ORDER';
    }

    if (this.isActivityReceivingLenses()) {
      return 'RECEIVED LENSES';
    }

    if (this.isActivityReceivingFrame()) {
      return 'RECEIVED FRAME';
    }

    if (this.isActivityOrderingFromStack()) {
      return 'ORDERED FRAME';
    }

    if (this.isActivityShipping()) {
      return 'SHIPPED';
    }

    if (this.isActivityDispensing()) {
      return 'ADD TO CART';
    }

    if (this.isActivitySendingRepair()) {
      return 'SENT FOR REPAIR';
    }

    const physical = this.orderForm?.get('products.0.frame.physicalStatus')?.value;
    if (physical === 'TO ORDER') {
      return 'SEND TO STACK';
    }
    if (physical === 'ORDERED') {
      return 'ORDERED FRAME';
    }
    if (physical === 'ON-APPROVAL') {
      return 'ORDERED ON APPROVAL';
    }

    const status = String(this.orderForm?.get('status')?.value || '');
    switch (job) {
      case 'SEND TO STACK':
        if (/^AWAITING /i.test(status) || status === 'RECEIVED' || status === 'DISPENSED') {
          return explicit || 'SAVE';
        }
        return 'SEND TO STACK';
      case 'HOLD / RX':
        return 'SAVE/HOLD';
      case 'HOLD':
        return 'HOLD';
      case 'CALL / RX':
      case 'CALL FOR RX':
        return 'CALL FOR RX';
      case 'INQUIRY':
        return 'INQUIRY';
      case 'REPAIR':
        if (
          /^AWAITING /i.test(status) ||
          status === 'RECEIVED' ||
          status === 'DISPENSED'
        ) {
          return explicit || 'SAVE';
        }
        if (
          isOutsideRepairLocation(
            String(this.orderForm?.get('products.0.frame.labVendor')?.value || '')
          )
        ) {
          return 'SAVE';
        }
        return 'TO REPAIR';
      case 'QUOTE':
        return 'SAVE/QUOTE';
      case 'CONVERT TO ORDER':
        return 'CONVERT TO ORDER';
      default:
        if (this.isJobActionChecked(ORDER_LENSES_ACTION) && this.lensStatusValue !== 'RECEIVED') {
          return 'ORDERED LENSES';
        }
        return explicit;
    }
  }

  getStackOrderStatusForType(type?: string): string {
    return stackStatusForType(type || this.orderForm?.get('type')?.value);
  }

  getAwaitingStatusForType(type?: string): string {
    const orderType = type || this.orderForm?.get('type')?.value;
    if (orderType === 'A' || orderType === 'F') {
      return combinedAwaitingStatus({
        type: orderType,
        frameAwaiting: true,
      });
    }
    return combinedAwaitingStatus({
      type: orderType,
      status: this.orderForm?.get('status')?.value,
      physicalStatus: this.orderForm?.get('products.0.frame.physicalStatus')?.value,
      jobAction: this.orderForm?.get('jobAction')?.value,
      jobActions: this.currentJobActions,
      lensStatus: this.lensStatusValue,
      frameAwaiting: true,
    });
  }

  get currentJobActions(): string[] {
    return jobActionList(
      this.orderForm?.get('jobAction')?.value,
      this.orderForm?.get('jobActions')?.value
    );
  }

  get lensStatusValue(): string {
    return String(this.orderForm?.get('lensStatus')?.value || '').trim();
  }

  private setJobActions(actions: string[]): void {
    const unique = Array.from(new Set(actions.filter(Boolean)));
    this.orderForm?.get('jobActions')?.setValue(unique);
    const exclusive = exclusiveJobAction(unique);
    this.orderForm?.get('jobAction')?.setValue(exclusive);
  }

  private applyOrderLenses(checked: boolean): void {
    if (checked) {
      const current = this.lensStatusValue;
      if (current !== 'RECEIVED') {
        const physical = String(
          this.orderForm?.get('products.0.frame.physicalStatus')?.value || ''
        ).trim();
        const status = String(this.orderForm?.get('status')?.value || '').trim();
        this.orderForm?.get('lensStatus')?.setValue(
          physical === 'ORDERED' || physical === 'ON-APPROVAL' || /^AWAITING /i.test(status)
            ? 'ORDERED'
            : 'TO ORDER'
        );
      }
      return;
    }
    if (this.lensStatusValue !== 'RECEIVED') {
      this.orderForm?.get('lensStatus')?.setValue('');
    }
  }

  private isFrameStillAwaiting(): boolean {
    const physical = String(
      this.orderForm?.get('products.0.frame.physicalStatus')?.value || ''
    ).trim();
    const status = String(this.orderForm?.get('status')?.value || '').trim();
    return (
      physical === 'ORDERED' ||
      physical === 'ON-APPROVAL' ||
      status === 'AWAITING FRAME' ||
      status === 'AWAITING PART' ||
      status === 'AWAITING ACCESSORY' ||
      status === 'AWAITING FRAME + LENSES'
    );
  }

  private ensureDefaultEta(): void {
    const eta = this.orderForm?.get('eta');
    if (!eta) {
      return;
    }
    const current = eta.value;
    if (current) {
      const parsed = current instanceof Date ? current : new Date(current);
      if (!isNaN(parsed.getTime())) {
        return;
      }
    }
    eta.setValue(
      new Date(
        Date.now() +
          FrameDetailsComponent.DEFAULT_ETA_DAYS * 24 * 60 * 60 * 1000
      )
    );
  }

  private etaTime(value: any): number | null {
    if (!value) {
      return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) {
      return null;
    }
    date.setHours(0, 0, 0, 0);
    return date.getTime();
  }

  private onEtaDateChange(value: any): void {
    const ms = this.etaTime(value);
    if (this.lastEtaMs == null) {
      this.lastEtaMs = ms;
      return;
    }
    if (ms === this.lastEtaMs) {
      return;
    }
    this.lastEtaMs = ms;
    this.resetEtaChecked();
    this.syncEtaCheckTask();
  }

  private resetEtaChecked(): void {
    const frame = this.orderForm?.get('products.0.frame');
    if (!frame?.get('etaChecked')?.value) {
      return;
    }
    frame.get('etaChecked')?.setValue(false);
    frame.get('etaCheckedBy')?.setValue('');
    frame.get('etaCheckedAt')?.setValue('');
    const activity = this.activityFormArray;
    if (!activity) {
      return;
    }
    for (let i = activity.length - 1; i >= 0; i--) {
      const group = activity.at(i) as FormGroup;
      const status = String(group.get('status')?.value || '').trim();
      if (
        status !== 'ORDERED' &&
        status !== 'SENT FOR REPAIR' &&
        status !== 'AWAITING FRAME'
      ) {
        continue;
      }
      if (group.get('completed')?.value) {
        group.patchValue({
          completed: false,
          completedBy: '',
          completedAt: '',
        });
      }
      break;
    }
  }

  private etaSeedExtra(referenceNumber: string): {
    referenceNumber?: string;
    eta?: any;
    completed?: boolean;
    completedBy?: string;
    completedAt?: any;
  } {
    return {
      referenceNumber,
      eta: this.orderForm?.get('eta')?.value || '',
      completed: !!this.orderForm?.get('products.0.frame.etaChecked')?.value,
      completedBy: this.orderForm?.get('products.0.frame.etaCheckedBy')?.value || '',
      completedAt: this.orderForm?.get('products.0.frame.etaCheckedAt')?.value || '',
    };
  }

  private backfillEtaOnOrderedActivity(): void {
    const activity = this.activityFormArray;
    if (!activity?.length) {
      return;
    }
    const eta = this.orderForm?.get('eta')?.value;
    const etaChecked = !!this.orderForm?.get('products.0.frame.etaChecked')?.value;
    const checkedBy =
      this.orderForm?.get('products.0.frame.etaCheckedBy')?.value || '';
    const checkedAt =
      this.orderForm?.get('products.0.frame.etaCheckedAt')?.value || '';
    for (let i = activity.length - 1; i >= 0; i--) {
      const group = activity.at(i) as FormGroup;
      const status = String(group.get('status')?.value || '').trim();
      if (
        status !== 'ORDERED' &&
        status !== 'SENT FOR REPAIR' &&
        status !== 'AWAITING FRAME'
      ) {
        continue;
      }
      if (eta && !group.get('eta')?.value) {
        if (group.get('eta')) {
          group.get('eta').setValue(eta, { emitEvent: false });
        } else {
          group.addControl('eta', this.fb.control(eta));
        }
      }
      if (etaChecked && !group.get('completed')?.value) {
        group.patchValue(
          {
            completed: true,
            completedBy: checkedBy,
            completedAt: checkedAt,
          },
          { emitEvent: false }
        );
      }
      break;
    }
  }

  isCompletableActivity(entry: { status?: string; completed?: boolean }): boolean {
    return isOpenTaskStatus(entry?.status);
  }

  completeActivityTask(index: number): void {
    const activity = this.activityFormArray;
    if (!activity || index < 0 || index >= activity.length) {
      return;
    }
    const group = activity.at(index) as FormGroup;
    if (group.get('completed')?.value) {
      return;
    }
    this.ensureInitialsFilled();
    const initials = this.frameNoteEditor;
    const now = new Date();
    group.patchValue({
      completed: true,
      completedBy: initials,
      completedAt: now,
    });
    group.markAsDirty();
    const status = String(group.get('status')?.value || '').trim();
    if (status === 'CHECK FRAME ETA') {
      this.orderForm?.get('products.0.frame.etaChecked')?.setValue(true);
      this.orderForm?.get('products.0.frame.etaCheckedBy')?.setValue(initials);
      this.orderForm?.get('products.0.frame.etaCheckedAt')?.setValue(now);
    }
    const action = Object.keys(TASK_STATUS_BY_ACTION).find(
      (key) => TASK_STATUS_BY_ACTION[key] === status || key === status
    );
    if (action && this.isJobActionChecked(action)) {
      this.setJobActions(toggleJobAction(this.currentJobActions, action, false));
    }
    if (action === 'CALL FOR RX') {
      this.orderForm?.get('rxStatus')?.setValue('');
    }
    this.saveOrder.emit({ destination: 'SAVE' });
  }

  private removeOpenTasks(statuses: string[]): void {
    const activity = this.activityFormArray;
    if (!activity || !statuses.length) {
      return;
    }
    const wanted = new Set(statuses.map((status) => this.taskLabel(status)));
    for (let i = activity.length - 1; i >= 0; i--) {
      const group = activity.at(i) as FormGroup;
      const status = String(group.get('status')?.value || '').trim();
      if (
        !wanted.has(this.taskLabel(status)) ||
        group.get('completed')?.value
      ) {
        continue;
      }
      activity.removeAt(i);
    }
  }

  private syncOpenTasksFromJobActions(): void {
    const activity = this.activityFormArray;
    if (!activity) {
      return;
    }
    const wanted = new Set<string>();
    this.currentJobActions.forEach((action) => {
      const status = TASK_STATUS_BY_ACTION[action];
      if (status) {
        wanted.add(status);
      }
    });
    wanted.forEach((status) => {
      const existing = activity.controls.find(
        (control) =>
          this.taskLabel(control.get('status')?.value) === this.taskLabel(status)
      );
      if (!existing) {
        const date =
          status === 'CALL / RX' || status === 'HOLD / RX'
            ? this.orderForm?.get('products.0.frame.followUpDate')?.value || new Date()
            : new Date();
        activity.push(this.activityEntryGroup(status, date));
      }
    });
    for (let i = activity.length - 1; i >= 0; i--) {
      const group = activity.at(i) as FormGroup;
      const status = String(group.get('status')?.value || '').trim();
      if (!isOpenTaskStatus(status) || group.get('completed')?.value) {
        continue;
      }
      if (
        status === 'INSTALL NOSEPADS' ||
        status === 'CHECK FRAME ETA' ||
        status === FIRST_INSPECTION_NEEDED ||
        status === 'FIRST INSPECTION' ||
        status === 'ORDER FRAME' ||
        status === 'ORDER PART' ||
        status === 'OTHER / PART' ||
        /^FOLLOW UP \//i.test(status)
      ) {
        continue;
      }
      const stillWanted = Array.from(wanted).some(
        (value) => this.taskLabel(value) === this.taskLabel(status)
      );
      if (!stillWanted) {
        activity.removeAt(i);
      }
    }
  }

  private syncEtaCheckTask(): void {
    const activity = this.activityFormArray;
    if (!activity) {
      return;
    }
    const existingIndex = activity.controls.findIndex((control) => {
      const status = String(control.get('status')?.value || '');
      return status === 'CHECK FRAME ETA' || /^FOLLOW UP \/ FRAME/i.test(status);
    });
    const due = this.isEtaCheckDue();
    if (due) {
      if (existingIndex === -1) {
        activity.push(
          this.activityEntryGroup(
            'FOLLOW UP / FRAME',
            this.orderForm?.get('eta')?.value || new Date(),
            { kind: 'open', followUpDate: this.orderForm?.get('eta')?.value }
          )
        );
      }
      return;
    }
    if (
      existingIndex >= 0 &&
      !activity.at(existingIndex).get('completed')?.value
    ) {
      activity.removeAt(existingIndex);
    }
  }

  private isEtaCheckDue(): boolean {
    if (this.orderForm?.get('products.0.frame.etaChecked')?.value) {
      return false;
    }
    const status = String(this.orderForm?.get('status')?.value || '').trim();
    if (
      ![
        'AWAITING FRAME',
        'AWAITING PART',
        'AWAITING ACCESSORY',
        'AWAITING FRAME + LENSES',
        'AWAITING LENSES',
        'AWAITING REPAIR',
      ].includes(status)
    ) {
      return false;
    }
    return this.etaTime(this.orderForm?.get('eta')?.value) != null
      ? this.etaTime(this.orderForm?.get('eta')?.value) <= this.etaTime(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000))
      : false;
  }

  private syncOtherPartActivity(checked: boolean): void {
    const activity = this.activityFormArray;
    if (!activity) {
      return;
    }
    const existingIndex = activity.controls.findIndex(
      (control) =>
        String(control.get('status')?.value || '') === 'OTHER / PART'
    );
    if (checked) {
      const description = String(
        this.orderForm?.get('products.0.frame.partOtherDescription')?.value || ''
      ).trim();
      if (existingIndex === -1) {
        activity.push(
          this.activityEntryGroup('OTHER / PART', new Date(), {
            kind: 'open',
            description,
          })
        );
      } else if (description) {
        const group = activity.at(existingIndex) as FormGroup;
        if (group.get('description')) {
          group.get('description').setValue(description);
        } else {
          group.addControl('description', this.fb.control(description));
        }
      }
      this.refreshTaskActivity();
      return;
    }
    if (existingIndex >= 0 && !activity.at(existingIndex).get('completed')?.value) {
      activity.removeAt(existingIndex);
    }
    this.refreshTaskActivity();
  }

  private syncNosepadActivity(checked: boolean): void {
    const activity = this.activityFormArray;
    if (!activity) {
      return;
    }
    const existingIndex = activity.controls.findIndex(
      (control) => String(control.get('status')?.value || '') === 'INSTALL NOSEPADS'
    );
    if (checked) {
      if (existingIndex === -1) {
        activity.push(this.activityEntryGroup('INSTALL NOSEPADS', new Date()));
      }
      return;
    }
    if (existingIndex >= 0 && !activity.at(existingIndex).get('completed')?.value) {
      activity.removeAt(existingIndex);
    }
  }

  ensureInitialsFilled(): void {
    const control = this.orderForm?.get('initials');
    if (!control) {
      return;
    }
    if (!String(control.value || '').trim()) {
      const initials = this.frameNoteEditor;
      if (initials) {
        control.setValue(initials);
      }
    }
  }

  get frameNoteEntries(): FrameNoteEntry[] {
    return this.parseFrameNotes(
      this.orderForm?.get('products.0.frame.notes')?.value
    );
  }

  get frameNoteEditor(): string {
    const formInitials = String(
      this.orderForm?.get('initials')?.value || ''
    ).trim();
    if (formInitials) {
      return formInitials.toUpperCase();
    }
    const userInitials = String(
      this.auth?.currentUser?.initials || ''
    ).trim();
    if (userInitials) {
      return userInitials.toUpperCase();
    }
    return String(this.auth?.initials || '').trim().toUpperCase();
  }

  get canAppendFrameNote(): boolean {
    return !!this.frameNoteDraft.trim() && !!this.frameNoteEditor;
  }

  appendFrameNote(): void {
    const text = this.frameNoteDraft.trim();
    const initials = this.frameNoteEditor;
    if (!text || !initials) {
      return;
    }

    const notesControl = this.orderForm?.get('products.0.frame.notes');
    if (!notesControl) {
      return;
    }

    const stamp = formatDate(new Date(), 'MM/dd/yyyy h:mm a', this.locale);
    const entry = `[${stamp} - ${initials}]\n${text}`;
    const existing = String(notesControl.value || '').trim();
    notesControl.setValue(existing ? `${existing}\n\n${entry}` : entry);
    notesControl.markAsDirty();
    this.frameNoteDraft = '';
  }

  private parseFrameNotes(raw: unknown): FrameNoteEntry[] {
    const text = String(raw || '').trim();
    if (!text) {
      return [];
    }

    const matches: Array<{
      index: number;
      timestamp: string;
      initials: string;
      headerLength: number;
    }> = [];
    const pattern = new RegExp(
      FrameDetailsComponent.FRAME_NOTE_ENTRY_START.source,
      'g'
    );
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(text)) !== null) {
      matches.push({
        index: match.index,
        timestamp: match[1],
        initials: match[2].trim(),
        headerLength: match[0].length,
      });
    }

    if (!matches.length) {
      return [{ timestamp: '', initials: '', text, legacy: true }];
    }

    const entries: FrameNoteEntry[] = [];
    if (matches[0].index > 0) {
      const preamble = text.slice(0, matches[0].index).trim();
      if (preamble) {
        entries.push({
          timestamp: '',
          initials: '',
          text: preamble,
          legacy: true,
        });
      }
    }

    for (let i = 0; i < matches.length; i++) {
      const start = matches[i].index + matches[i].headerLength;
      const end =
        i + 1 < matches.length ? matches[i + 1].index : text.length;
      entries.push({
        timestamp: matches[i].timestamp,
        initials: matches[i].initials,
        text: text.slice(start, end).replace(/^\n+/, '').trim(),
      });
    }

    return entries;
  }

  get followUpDateLabel(): string {
    const status = this.orderForm?.get('products.0.frame.followUpStatus')?.value;
    switch (status) {
      case 'RETURNED':
        return 'RETURN DATE';
      case 'WARRANTY REPLACEMENT':
        return 'DATE SENT';
      case 'REPAIR':
        return 'REPAIR DATE';
      default:
        return 'DATE';
    }
  }
}
